import { useEffect, useState } from 'react'
import type { TabType } from 'app/components/Tabs'
import { gqlFetchList } from 'app/lib/graphqlClient'
import { useWorkspace } from 'app/utils'
import { bg } from 'lib/background'
import { logWarn } from 'lib/logging'

export const SMS_TEMPLATES_TAB: TabType = { name: `SMS`, href: `/settings/templates/sms` }
export const TASK_TEMPLATES_TAB: TabType = { name: `Tasks`, href: `/settings/templates/tasks` }
export const NOTE_TEMPLATES_TAB: TabType = { name: `Notes`, href: `/settings/templates/notes` }

export function templateTabsFor(smsEnabled: boolean | undefined): TabType[] {
  const rest = [TASK_TEMPLATES_TAB, NOTE_TEMPLATES_TAB]
  return smsEnabled === false ? rest : [SMS_TEMPLATES_TAB, ...rest]
}

/** The product a location needs before SMS templates are useful to it. */
const INBOX_PRODUCT = `Inbox`

export type LocationProducts = { products?: string[] | null }

/** SMS templates are offered when any location in the workspace sells Inbox. */
export function smsTemplatesEnabled(locations: LocationProducts[]): boolean {
  return locations.some((location) => location.products?.includes(INBOX_PRODUCT) ?? false)
}

export type TemplateTabsState = {
  tabs: TabType[]
  /** Undefined until the locations query answers. */
  smsEnabled: boolean | undefined
}

/**
 * The templates hub's tab set, gated by workspace capability: SMS templates
 * only appear when at least one location in the workspace has the Inbox
 * product. While the answer is unknown every tab renders, so an enabled
 * workspace never sees the SMS tab flicker in. Tasks and Notes are always on.
 */
export function useTemplateTabs(): TemplateTabsState {
  const workspace = useWorkspace()
  const [smsEnabled, setSmsEnabled] = useState<boolean | undefined>()

  useEffect(() => {
    if (!workspace?.id) return
    // Search hoisted out of the call: the no-inline-graphql-query rule flags
    // any template literal inside gql helper arguments.
    const locationSearch = `workspaceId:${workspace.id}`
    bg(
      gqlFetchList<{ id: string; products?: string[] }>(
        'locations',
        { search: locationSearch, pagination: { page: 0, limit: 200 } },
        'id products',
      )
        .then(({ hits }) => {
          setSmsEnabled(smsTemplatesEnabled(hits ?? []))
        })
        // Deliberately does NOT resolve to false: a failed locations lookup is
        // not evidence that the workspace lacks Inbox, and hiding a tab the
        // workspace pays for is worse than showing one it does not. Staying
        // undefined keeps the permissive default (both tabs), and the catch is
        // still needed so the rejection is not unhandled.
        .catch((err) => logWarn(`useTemplateTabs: locations lookup failed, showing both tabs`, err)),
    )
  }, [workspace?.id])

  return { tabs: templateTabsFor(smsEnabled), smsEnabled }
}
